import { number2ordinal } from "@/Common/helpers/number";
import { allowedStatuses, noRanking, ordinalOffset } from "../constants";
import cloneDeep from "lodash-es/cloneDeep";

function getUsersInCommissionCycleNode(month) {
	const payPeriods = month?.list || null;
	if (payPeriods === null) {
		return 0;
	}

	return payPeriods.reduce((maxUsers, period) => {
		const length = period.list.length;
		return Math.max(maxUsers, length);
	}, 0);
}

export const addWashoutSubscriptions = async (
	_position,
	tree = [],
	subscriptionsInformation = {
		subscription: {},
		onNewWashout: {},
		onUpdateWashout: {},
	}
) => {
	const newActiveCommissionCycles = [];
	for (const year of tree) {
		const filteredYear = year.list.filter(({ status }) => allowedStatuses.includes(status));
		for (const month of filteredYear) {
			subscriptionsInformation?.subscription?.hook?.(
				[subscriptionsInformation?.subscription?.context, month._id],
				[
					{
						context: subscriptionsInformation?.onNewWashout?.context,
						query: subscriptionsInformation?.onNewWashout?.query?.(),
						variables: {
							commissionCycleId: month._id,
						},
					},
					{
						context: subscriptionsInformation?.onUpdateWashout?.context,
						query: subscriptionsInformation?.onUpdateWashout?.query?.(),
						variables: {
							commissionCycleId: month._id,
						},
					},
				]
			);
			newActiveCommissionCycles.push({
				_id: month._id,
				usersInCommissionCycle: getUsersInCommissionCycleNode(month),
				status: month.status,
			});
		}
	}
	return newActiveCommissionCycles;
};

const findUpdatedCommissionCycleNodeInTree = (tree, commissionCycleId) => {
	const yearNodeFoundIn = tree?.find?.((year) => year?.list?.find?.((month) => month?._id === commissionCycleId));
	if (!yearNodeFoundIn) {
		return null;
	}
	return yearNodeFoundIn?.list?.find?.((month) => month?._id === commissionCycleId);
};

const updateUserInPayPeriod = (userWashout, payPeriodNode, highestValidRank) => {
	const usersInPayPeriod = payPeriodNode?.list;
	const targetUser = usersInPayPeriod.find((user) => user?.userId === userWashout?.employee?._id);
	usersInPayPeriod
		.filter((user) => !user.rank && user?.information?.title)
		.forEach((user) => {
			user.rank = +String(user.information.title).slice(0, ordinalOffset);
		});
	if (targetUser) {
		const informationInPayPeriodNode = targetUser.information.title;
		const newUsersWashoutTitle = number2ordinal(+userWashout.employee.ranking);
		if (informationInPayPeriodNode !== newUsersWashoutTitle) {
			const newRank = +userWashout.employee.ranking;
			if (newRank === noRanking) {
				targetUser.information.title = highestValidRank;
			} else {
				targetUser.information.title = number2ordinal(newRank);
			}
			targetUser.rank = newRank;
		}
	}
	return targetUser;
};

const incomingWashoutBelongsToSelectedNode = (washout, selected) => {
	return (
		washout?.employee?._id === selected?.userId &&
		washout?.commissionCycleId === selected?.commissionCycleId &&
		washout?.payPeriodId === selected?.payPeriodId
	);
};

const updateSidePanelCallBackIfNeeded = (activeCommissionCycle, props) => {
	if (activeCommissionCycle && activeCommissionCycle?.usersLoadedInCC === activeCommissionCycle?.usersInCommissionCycle) {
		props?.updateSidePanelCallBack?.(props.selected.current);
	}
};

const handleNewWashoutSubscription = ({ washoutUpdated, subscriptionsInformation, props, actions, options }) => {
	if (!washoutUpdated) {
		return null;
	}
	const subscriptionReference = [
		"getWashout",
		washoutUpdated?.payPeriodId,
		props.selected?.position,
		washoutUpdated?.userId,
		washoutUpdated?.commissionCycleId,
	];

	let activeCommissionCycle = null;
	let tree = actions?.queryClient?.getQueryData?.([subscriptionsInformation?.subscription?.treeContext, props.location?.locationId]);
	if (tree) {
		const activeCommissionCycles = tree.activeCommissionCycles;
		let newTreeData = {
			activeCommissionCycles,
			tree: tree.tree,
		};
		activeCommissionCycle = activeCommissionCycles.find((CC) => CC._id === washoutUpdated.commissionCycleId);
		if (activeCommissionCycle) {
			activeCommissionCycle.usersInCommissionCycleLoaded = (activeCommissionCycle.usersInCommissionCycleLoaded || 0) + 1;
		}
		if (options?.useRanking) {
			tree = tree.tree;
			const commissionCycleNodeToUpdate = findUpdatedCommissionCycleNodeInTree(tree, washoutUpdated.commissionCycleId);
			const payPeriodNodesToUpdate = commissionCycleNodeToUpdate?.list?.filter?.((payPeriodNode) => {
				const payPeriodId = String(payPeriodNode._id).split("_")?.[1];
				return washoutUpdated?.payPeriodId === payPeriodId;
			});
			payPeriodNodesToUpdate
				?.filter?.((payPeriodNode) => Boolean(payPeriodNode))
				.forEach((payPeriodNodeToUpdate) => {
					let highestValidRank = payPeriodNodeToUpdate.list[payPeriodNodeToUpdate.list.length - 1].information.title;
					highestValidRank = number2ordinal(+String(highestValidRank).slice(0, ordinalOffset));
					updateUserInPayPeriod(washoutUpdated, payPeriodNodeToUpdate, highestValidRank);
				});
			newTreeData = {
				activeCommissionCycles,
				tree: [...tree],
			};
		}
		actions?.queryClient?.setQueryData?.(
			[subscriptionsInformation?.subscription?.treeContext, props.location?.locationId],
			newTreeData
		);
	}
	const previousWashout = actions?.queryClient?.getQueryData?.(subscriptionReference);
	if (previousWashout) {
		actions?.queryClient?.refetchQueries({
			queryKey: subscriptionReference,
			type: "active",
		});
	} else if (incomingWashoutBelongsToSelectedNode(washoutUpdated, props.selected)) {
		actions?.queryClient?.setQueryData?.(subscriptionReference, () => washoutUpdated);
	}
	updateSidePanelCallBackIfNeeded(activeCommissionCycle, props);
	return washoutUpdated;
};

const handleUpdatedWashoutSubscription = ({ washoutUpdated, props, actions }) => {
	if (washoutUpdated) {
		actions?.queryClient?.setQueriesData?.(
			{ queryKey: ["getWashout", washoutUpdated?.payPeriodId, props.selected?.position] },
			(oldData) => {
				const newData = cloneDeep(oldData);
				if (newData?._id === washoutUpdated?._id) {
					newData.summary = washoutUpdated.summary; // update washout data display
				}
				return newData;
			}
		);
	}

	return washoutUpdated;
};

export function handleWashoutSubscriptions({
	props = {},
	subscriptionsInformation = {},
	actions = {
		getSubscription: null,
		queryClient: null,
	},
	options = {
		useRanking: false,
	},
}) {
	for (const CC of props.activeCommissionCycles) {
		const positionSubscription = actions?.getSubscription?.([subscriptionsInformation?.subscription?.context, CC._id]);
		if (positionSubscription) {
			const newWashoutQuery = subscriptionsInformation?.onNewWashout?.context;
			const updateWashoutQuery = subscriptionsInformation?.onUpdateWashout?.context;

			positionSubscription?.subscriptions?.[newWashoutQuery]?.setCallback((result) => {
				const washoutUpdated = result.data?.[newWashoutQuery] || null;
				handleNewWashoutSubscription({ washoutUpdated, subscriptionsInformation, props, actions, options });
			});
			positionSubscription?.subscriptions?.[updateWashoutQuery]?.setCallback((result) => {
				const washoutUpdated = result.data?.[updateWashoutQuery];
				handleUpdatedWashoutSubscription({ washoutUpdated, props, actions });
			});
		}
	}
}
